import React from 'react';
import { Minus, Plus } from 'lucide-react';

export interface NumberStepperProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  hint?: string;
  suffix?: string;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Bounded numeric input with a slider. Used where a value is a quantity rather
 * than a choice: onboard outputs, wattage.
 */
export const NumberStepper: React.FC<NumberStepperProps> = ({
  label,
  value,
  min,
  max,
  onChange,
  hint,
  suffix,
}) => (
  <div>
    <div className="mb-1.5 flex items-baseline justify-between">
      <span className="text-sm font-medium text-[#333333]">{label}</span>
      <span className="font-display text-xl text-[#D4A054]">
        {value}
        {suffix && <span className="ml-1 text-sm text-[#666666]">{suffix}</span>}
      </span>
    </div>

    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label={`Decrease ${label}`}
        disabled={value <= min}
        onClick={() => onChange(clamp(value - 1, min, max))}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E0E0E0] text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#D4A054] disabled:opacity-40 disabled:hover:border-[#E0E0E0] disabled:hover:text-[#666666]"
      >
        <Minus className="h-4 w-4" />
      </button>

      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(clamp(Number(event.target.value), min, max))}
        className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-[#E0E0E0] accent-[#D4A054]"
      />

      <button
        type="button"
        aria-label={`Increase ${label}`}
        disabled={value >= max}
        onClick={() => onChange(clamp(value + 1, min, max))}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E0E0E0] text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#D4A054] disabled:opacity-40 disabled:hover:border-[#E0E0E0] disabled:hover:text-[#666666]"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>

    {hint && <span className="mt-1.5 block text-xs text-[#666666]">{hint}</span>}
  </div>
);

export default NumberStepper;
